import { MAX_LINE_UNITS, assertCents, assertUnits } from './units.js';
/**
 * Discards packs that cannot ever be part of a cheapest answer.
 *
 * A pack of one is not a pack, and a pack costing at least as much as the same
 * number of singles is never worth taking. Filtering it out here rather than
 * letting the comparison below reject it means an admin typo — "10 for $250" —
 * becomes a no-op instead of an overcharge, and it stays impossible for a line
 * to be priced above the single-price total by construction rather than by
 * clamping a negative discount away afterwards.
 */
function usablePacks(packs, unitPriceCents) {
    return packs.filter((pack) => pack.packUnits >= 2 && pack.packPriceCents < pack.packUnits * unitPriceCents);
}
/**
 * Cheapest way to buy each quantity from 0 to `maxUnits`.
 *
 * This is min-cost coin change: to fill `i` units you either add one single to
 * the best filling of `i - 1`, or close a pack of size `s` over the best filling
 * of `i - s`. Taking the minimum at every step means the answer for the whole
 * line is optimal over every possible mix, so no combination a customer could
 * assemble by hand can beat what they are charged.
 *
 * Solved for every quantity up to `maxUnits`, not just the one asked for,
 * because `findPackOpportunity` needs the neighbouring totals and recomputing
 * the table for each of them would be quadratic for no reason.
 */
function solve(maxUnits, unitPriceCents, packs) {
    const usable = usablePacks(packs, unitPriceCents);
    const best = new Array(maxUnits + 1).fill(0);
    const viaPack = new Array(maxUnits + 1).fill(null);
    for (let i = 1; i <= maxUnits; i += 1) {
        // Written on an earlier pass, so the fallbacks below are unreachable — they
        // are here because the compiler cannot see that an index is in range.
        let cheapest = (best[i - 1] ?? 0) + unitPriceCents;
        let chosen = null;
        for (const pack of usable) {
            if (pack.packUnits > i)
                continue;
            const candidate = (best[i - pack.packUnits] ?? 0) + pack.packPriceCents;
            if (candidate < cheapest) {
                cheapest = candidate;
                chosen = pack;
            }
        }
        best[i] = cheapest;
        viaPack[i] = chosen;
    }
    return { best, viaPack };
}
/** Walks the solver's choices back from a quantity to the packs that made it up. */
function reconstruct(solution, quantityUnits) {
    const counts = new Map();
    let looseUnits = 0;
    let cursor = quantityUnits;
    while (cursor > 0) {
        const pack = solution.viaPack[cursor] ?? null;
        if (pack === null) {
            looseUnits += 1;
            cursor -= 1;
            continue;
        }
        const seen = counts.get(pack.packUnits);
        counts.set(pack.packUnits, {
            packUnits: pack.packUnits,
            packPriceCents: pack.packPriceCents,
            count: (seen?.count ?? 0) + 1,
        });
        cursor -= pack.packUnits;
    }
    return {
        // Largest pack first, which is the order a receipt reads best in.
        packsApplied: [...counts.values()].sort((a, b) => b.packUnits - a.packUnits),
        looseUnits,
        lineTotalCents: solution.best[quantityUnits] ?? 0,
    };
}
function validate(input) {
    assertUnits(input.quantityUnits, 'quantityUnits');
    assertCents(input.unitPriceCents, 'unitPriceCents');
    if (input.quantityUnits > MAX_LINE_UNITS) {
        throw new RangeError(`quantityUnits must be at most ${MAX_LINE_UNITS}, got ${input.quantityUnits}`);
    }
    for (const pack of input.packs) {
        assertUnits(pack.packUnits, 'pack.packUnits');
        assertCents(pack.packPriceCents, 'pack.packPriceCents');
    }
}
/** Prices one cart line. The server recomputes this from the database at purchase time. */
export function priceLine(input) {
    validate(input);
    const solution = solve(input.quantityUnits, input.unitPriceCents, input.packs);
    const filled = reconstruct(solution, input.quantityUnits);
    return {
        quantityUnits: input.quantityUnits,
        unitPriceCents: input.unitPriceCents,
        ...filled,
        // Every term is an integer number of cents an admin typed, so this is exact.
        // There is no rounding anywhere in this engine, and nothing to round: the
        // per-kilo rates that used to produce fractional cents are gone.
        lineDiscountCents: input.quantityUnits * input.unitPriceCents - filled.lineTotalCents,
    };
}
/**
 * Prices a whole cart.
 *
 * `subtotal` is what the lines would cost at single prices and `total` is what
 * is actually charged, so `subtotal - discount === total` holds by construction
 * and a receipt can show the saving without recomputing anything.
 */
export function priceCart(inputs) {
    const lines = inputs.map(priceLine);
    const totalCents = lines.reduce((sum, line) => sum + line.lineTotalCents, 0);
    const discountCents = lines.reduce((sum, line) => sum + line.lineDiscountCents, 0);
    return { lines, subtotalCents: totalCents + discountCents, discountCents, totalCents };
}
/**
 * Finds the "add one more and pay less" nudge for a line, if one exists.
 *
 * Flat pack pricing has a cliff at every pack size: nine units at $20 each is
 * $180, while ten units is a $150 pack. Buying more costs less. That is inherent
 * to selling packs at all, not a bug — so rather than hide it, the basket
 * surfaces it. A customer who works it out afterwards feels tricked; one who is
 * told "add 1 to save $30.00" feels looked after.
 *
 * Only quantities up to one pack beyond the current one are considered, and
 * that is provably enough: if some far larger quantity were cheaper, you could
 * delete any single item from its optimal filling to get a smaller quantity that
 * is cheaper still, and repeat until you landed within one pack of where the
 * customer already is. So a cheaper total further out always has a cheaper
 * total inside the scanned window.
 *
 * Among several opportunities it returns the best saving PER EXTRA UNIT, with
 * ties going to the shorter reach. Ranking by absolute saving instead would push
 * someone holding nine units towards a 24-case to save a dollar more than the
 * 10-pack saves them — technically the larger number, but a shop that upsells
 * that hard stops being believed. Change the comparison below to
 * `savingCents > best.savingCents` for the pushier behaviour.
 */
export function findPackOpportunity(input) {
    validate(input);
    const usable = usablePacks(input.packs, input.unitPriceCents);
    const largestPack = usable.reduce((max, pack) => Math.max(max, pack.packUnits), 0);
    if (largestPack === 0)
        return null;
    const ceiling = Math.min(input.quantityUnits + largestPack, MAX_LINE_UNITS);
    const solution = solve(ceiling, input.unitPriceCents, input.packs);
    const currentTotal = solution.best[input.quantityUnits] ?? 0;
    let best = null;
    for (let target = input.quantityUnits + 1; target <= ceiling; target += 1) {
        const targetTotalCents = solution.best[target] ?? 0;
        const savingCents = currentTotal - targetTotalCents;
        if (savingCents <= 0)
            continue;
        const extraUnits = target - input.quantityUnits;
        if (best === null) {
            best = { extraUnits, targetUnits: target, targetTotalCents, savingCents };
            continue;
        }
        // saving/extra > best.saving/best.extra, cross-multiplied so the comparison
        // stays in integers and never depends on a float division rounding.
        const better = savingCents * best.extraUnits - best.savingCents * extraUnits;
        if (better > 0 || (better === 0 && extraUnits < best.extraUnits)) {
            best = { extraUnits, targetUnits: target, targetTotalCents, savingCents };
        }
    }
    return best;
}
//# sourceMappingURL=pricing.js.map